// noinspection JSCommentMatchesSignature

import authentication from '@/modules/authentication/api';
import router from '@/router';
import overlord, { overlordConnect } from '@/overlord';
import { isLoginPage } from '@/router/utils';
import requestGoogleOauthAccessToken from '@/modules/authentication/store/google-oauth-token-client';
import {
  LOGIN_PAGE_ID_CHANGE_PASSWORD, LOGIN_PAGE_ID_CONFIGURE_SECOND_FACTOR,
  LOGIN_PAGE_ID_GOOGLE_LOGIN, LOGIN_PAGE_ID_SECOND_FACTOR, LOGIN_PAGE_NAME,
} from '@/modules/authentication/config/login-routes';
import { loginResultFromError, loginResultFromResponse } from './format-login-result';

export default {
  namespaced: true,
  state: {
    staffIsLogged: true,

    // Data needed to change the staff's password
    userNick: null,
    oldPassword: null,

    // Data needed to configure 2FA
    secondFactorJwt: null,
    otpProvisioningUri: null,
  },
  mutations: {
    SET_STAFF_IS_LOGGED: (state, status) => {
      state.staffIsLogged = !!status;
    },
    SET_SECOND_FACTOR_DATA(state, { jwt, otpProvisioningUri }) {
      state.secondFactorJwt = jwt;
      state.otpProvisioningUri = otpProvisioningUri;
    },
    SET_CHANGE_PASSWORD_DATA(state, { nick, password }) {
      state.userNick = nick;
      state.oldPassword = password;
    },
    RESET(state) {
      state.secondFactorJwt = null;
      state.otpProvisioningUri = null;
      state.userNick = null;
      state.oldPassword = null;
    },
  },
  actions: {
    async UPDATE_IS_LOGGED({ commit }, isLogged) {
      commit('SET_STAFF_IS_LOGGED', isLogged);
    },

    /**
     * Logs the user in.
     * @param customLoginService
     * @param {{ nick: string, password: string }} payload
     * @return {Promise<LoginResult>}
     */
    async LOGIN_STAFF({ dispatch, commit }, { customLoginService, ...payload }) {
      await overlordConnect();

      commit('SET_CHANGE_PASSWORD_DATA', payload);

      return (customLoginService
        ? customLoginService(payload)
        : authentication.login(payload))
        .then(loginResultFromResponse, loginResultFromError)
        .then(result => dispatch('HANDLE_LOGIN_RESULT', { result }));
    },

    /**
     * Verifies the user's second-factor code.
     * @param {string} otp One Time Password given by the external authentication app.
     * @return {Promise<LoginResult>}
     */
    async SECOND_FACTOR_VERIFICATION({ dispatch, state }, { otp }) {
      await overlordConnect();

      const payload = { jwt: state.secondFactorJwt, otp };
      return authentication
        .verifySecondFactor(payload)
        .then(loginResultFromResponse, loginResultFromError)
        .then(result => dispatch('HANDLE_LOGIN_RESULT', { result }));
    },

    /**
     * Changes the user's password.
     * It only works if the previous password has been stored in `state.oldPassword`.
     * @param {string} newPassword
     * @return {Promise<LoginResult>}
     */
    async CHANGE_PASSWORD({ dispatch, state }, { newPassword }) {
      await overlordConnect();

      const payload = {
        nick: state.userNick,
        oldPassword: state.oldPassword,
        newPassword,
      };

      return authentication
        .changePassword(payload)
        .then(loginResultFromResponse, loginResultFromError)
        .then(result => dispatch('HANDLE_LOGIN_RESULT', { result, payload }));
    },

    /**
     * Logs the user in with a token provided by Google.
     * @return {Promise<LoginResult>}
     */
    async GOOGLE_LOGIN({ dispatch, rootState }) {
      await overlordConnect();

      return requestGoogleOauthAccessToken(rootState.bootstrap.staffGoogleLoginClientId)
        .then(code => authentication.googleLogin({ code }))
        .then(loginResultFromResponse, (error) => {
          if (error.response) {
            return loginResultFromError(error);
          }

          return {
            success: false,
            statusCode: 500,
            response: {},
            apiCode: 'google-error',
            message: '',
          };
        })
        .then(result => dispatch('HANDLE_LOGIN_RESULT', { result }));
    },

    /**
     * Initialize password-recovery, sending an email to the user with the instructions to follow.
     * @param {string} email
     * @return {Promise<LoginResult>}
     */
    async REQUEST_PASSWORD_RECOVERY(ctx, { email }) {
      return authentication.requestPasswordRecovery({ email })
        .then(loginResultFromResponse, loginResultFromError);
    },

    /**
     * Allows the user to create a new password.
     * @param {string} jwt JWT given by API to the user via email.
     * @param {string} newPassword
     * @return {Promise<LoginResult>}
     */
    async RECOVER_PASSWORD(ctx, { jwt, newPassword }) {
      return authentication.recoverPassword({ jwt, newPassword })
        .then(loginResultFromResponse, loginResultFromError);
    },

    /**
     * Initialize second-factor recovery, showing a new QR code to the user for him to scan and validate.
     * @return {Promise<LoginResult>}
     */
    async REQUEST_SECOND_FACTOR_RESET({ commit }) {
      /** @var {LoginResult} result */
      const result = await authentication.requestSecondFactorReset()
        .then(loginResultFromResponse, loginResultFromError);
      if (result.success
        && result.response.code === '2fa-require-configuration'
      ) {
        commit('SET_SECOND_FACTOR_DATA', result.response);
      }

      return result;
    },

    /**
     * Finalize second-factor recovery.
     * @param {string} otp One Time Password given by the external authentication app.
     * @return {Promise<LoginResult>}
     */
    async SECOND_FACTOR_RESET({ commit, state }, { otp }) {
      const payload = { jwt: state.secondFactorJwt, otp };
      /** @var {LoginResult} result */
      const result = await authentication
        .configureSecondFactor(payload)
        .then(loginResultFromResponse, loginResultFromError);
      if (result.success) {
        commit('RESET');
      }
      return result;
    },

    /**
     * Invalidates a staff login credentials, forcing them to change their password and second-factor.
     * @param {number} staffId
     * @return {Promise<LoginResult>}
     */
    async INVALIDATE_STAFF_CREDENTIALS(ctx, staffId) {
      return authentication.invalidateStaffCredentials(staffId)
        .then(loginResultFromResponse, loginResultFromError);
    },

    /**
     * Process the result of the login flow, checking if the request was successful or not, and redirecting the user to
     * the proper login page if necessary.
     * @param {LoginResult} result
     * @return {Promise<LoginResult>}
     * @private
     */
    async HANDLE_LOGIN_RESULT({ commit, dispatch }, { result }) {
      console.log('handle login result', result);
      const currentPageIsLogin = isLoginPage(router.currentRoute.name);
      const redirectTo = currentPageIsLogin
        ? router.currentRoute.query.redirectTo || '/'
        : router.currentRoute.path;

      if (result.success
        && result.apiCode === 'ok'
      ) {
        // Staff login completed successfully
        commit('RESET');
        commit('SET_STAFF_IS_LOGGED', true);

        // Refresh app config, user and user permissions could be changed
        await dispatch('LOAD_BASE_CONFIG', null, { root: true });

        // Navigate to home page if user is in login page
        if (currentPageIsLogin) {
          result.redirect = { path: redirectTo };
        }

        return result;
      }

      switch (result.apiCode) {
        case 'social-login-google-required':
          result.redirect = {
            name: LOGIN_PAGE_ID_GOOGLE_LOGIN,
            query: { redirectTo },
          };
          break;
        case 'expired-password':
          result.redirect = {
            name: LOGIN_PAGE_ID_CHANGE_PASSWORD,
            query: { redirectTo },
          };
          break;
        case '2fa-require-configuration':
          commit('SET_SECOND_FACTOR_DATA', result.response);
          result.redirect = {
            name: LOGIN_PAGE_ID_CONFIGURE_SECOND_FACTOR,
            query: { redirectTo },
          };
          break;
        case '2fa-login':
          commit('SET_SECOND_FACTOR_DATA', result.response);
          result.redirect = {
            name: LOGIN_PAGE_ID_SECOND_FACTOR,
            query: { redirectTo },
          };
          break;
        case 'error':
        case 'recovery-token-invalid':
        case 'invalid-password':
          break;
        default:
          console.error('Unknown error when performing login:', result);
      }

      return result;
    },

    async LOGIN_STAFF_WITH_KEYCLOAK_TOKEN({ commit, dispatch }, { token }) {
      await overlordConnect();

      return authentication.keycloakLogin({ token })
        .then(loginResultFromResponse, loginResultFromError)
        .then(result => dispatch('HANDLE_LOGIN_RESULT', { result }));
    },

    async LOGOUT_STAFF({ dispatch, commit }) {
      let response = null;
      try {
        response = await authentication.logout();
      } catch (error) {
        return { errors: error.response.data.errors };
      }

      // Auth in OverlordJS
      await dispatch('auth/logout', null, { root: true });
      overlord.io.disconnect();

      commit('SET_STAFF_IS_LOGGED', false);

      router.push({ name: LOGIN_PAGE_NAME });

      return response;
    },

    /** @deprecated in favor of new LOGIN_STAFF action */
    async OLD_LOGIN_STAFF({ commit, dispatch }, { nick, password, customLoginService }) {
      // login
      let response = null;
      try {
        if (overlord.io.disconnected) {
          await overlord.io.open();
        }
        const payload = { nick, password };
        response = customLoginService
          ? await customLoginService(payload)
          : await authentication.login(payload);
      } catch (error) {
        return {
          errors: error.response.data.errors,
          statusCode: error.response.status,
          message: error.response.data.message,
          cmsDialog: error.response.data.cmsDialog,
        };
      }

      // User is logged
      commit('SET_STAFF_IS_LOGGED', true);

      // Refresh app config, user and user permisions could be changed
      await dispatch('LOAD_BASE_CONFIG', null, { root: true });

      // Navigate to home page if user is in login page
      if (router.currentRoute.name === 'login') {
        router.push(router.currentRoute.query.redirectTo || '/');
      }

      return response;
    },
    /** @deprecated In favor of new `CHANGE_PASSWORD` action. */
    async OLD_CHANGE_PASSWORD({ commit, dispatch }, { nick, newPassword, oldPassword }) {
      // login
      let response = null;
      try {
        if (overlord.io.disconnected) {
          await overlord.io.open();
        }
        const payload = { nick, newPassword, oldPassword };
        await authentication.changePassword(payload);
        response = {
          statusCode: 200,
          errors: [],
          message: 'ok',
        };
      } catch (error) {
        return {
          errors: error.response.data.errors,
          statusCode: error.response.status,
          message: error.response.data.message,
        };
      }

      // User is logged
      commit('SET_STAFF_IS_LOGGED', true);

      // Refresh app config, user and user permisions could be changed
      await dispatch('LOAD_BASE_CONFIG', null, { root: true });

      // Navigate to home page if user is in login page
      if (router.currentRoute.name === 'login') {
        router.push(router.currentRoute.query.redirectTo || '/');
      }

      return response;
    },
    /** @deprecated In favor of new `GOOGLE_LOGIN` action. */
    async OLD_GOOGLE_LOGIN_STAFF({ commit, dispatch }, { code }) {
      // google login
      let response = null;
      try {
        if (overlord.io.disconnected) {
          overlord.io.open();
        }
        response = await authentication.googleLogin({ code });
      } catch (error) {
        return { errors: error.response.data.errors };
      }

      // User is logged
      commit('SET_STAFF_IS_LOGGED', true);

      // Refresh app config, user and user permisions could be changed
      await dispatch('LOAD_BASE_CONFIG', null, { root: true });

      // Navigate to home page if user is in login page
      if (router.currentRoute.name === 'login') {
        router.push(router.currentRoute.query.redirectTo || '/');
      }

      return response;
    },
  },
  getters: {
    staffIsLogged: state => state.staffIsLogged,
  },
};
